/**
 * Trump Watch, company-first: which names he has touched, and what the value screen says. D81.
 *
 * 🔴 **The unit is the COMPANY, not the statement, and that inversion is the whole design.** The
 * statement feed answers *"what did he say and what happened after"*. A reader does not arrive with
 * that question; they arrive with *"is there anything here I should look at"*. A basket of eighteen
 * drone names is eighteen rows here and one row there, and the one-row version buries the two names
 * that matter under sixteen that do not. So the statement is demoted to the last column, as
 * evidence for why a company is on the page.
 *
 * ⛔ **Exposure is a reason to look. The screen is the reason to buy.** Every verdict on this page
 * comes from the value screen, was formed without reference to anything he said, and does not move
 * because he said it. No rule reads this screen.
 *
 * ## 🔴 Four groups, where the mockup drew three
 *
 * `NONE` and *no pick row* are different facts and the page must not merge them. `NONE` is the
 * screener saying it looked and declined — 2,481 of the newest production run's 2,884 rows, by far
 * its most common verdict. No row means it was never evaluated: the daily job scores only names
 * with a Trading 212 price line, so 2,870 of 8,560 universe members have one. ⛔ Merging them
 * would report a rejection the screener never made, on names like `INTC` and `IBM` that it has
 * never looked at.
 *
 * ## 🔴 Where this deliberately departs from the mockup, and why
 *
 * The mockup colours the group headings `#3FB27F` and `#E3C74A` — which are `--profit` and
 * `--attn`, the two tokens the design system most explicitly reserves. Every semantic colour is
 * *bound to a non-colour channel*: profit green carries a sign or an arrow, and attention-yellow
 * "never fills anything except the footer's stale-data tint". A heading has no sign to bind to, so
 * green there would spend the P&L colour on something that is not a P&L figure — and the third
 * colour law is product truth, not a preference.
 *
 * ⚠ So the distinction is carried by the channels the mockup already provides and that survive
 * greyscale and deuteranopia: the **spelled-out labels**, the **reading order**, and `--accent` on
 * the one group that matters. `--profit`/`--loss` appear only inside `Num`, on the move, where the
 * sign binds them. `--attn` appears only on a stale age chip with its `triangle-alert`, which is
 * the readme's own sanctioned use.
 */
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { AgeChip } from '../../design/components/data/AgeChip.jsx';
import { EmptyState } from '../../design/components/messages/EmptyState.jsx';
import { MetricTile } from '../../design/components/data/MetricTile.jsx';
import { Num } from '../../design/components/data/Num.jsx';

export type TrumpWatchRow = {
  ticker: string;
  company: string;
  isin?: string | null;
  theme: string;
  phrase?: string | null;
  exposureHits?: number | null;
  newestFiling?: string | null;
  exposed?: boolean | null;
  exposureRank?: number | null;
  verdict?: string | null;
  verdictReason?: string | null;
  verdictGroup: string;
  screenerOn?: string | null;
  marketCap?: number | null;
  shareCountFiled?: string | null;
  capStale?: boolean | null;
  pctSince?: string | null;
  moveSigma?: string | null;
  lastPriceAt?: string | null;
  readStale?: boolean | null;
  statementId: number;
  statement: string;
  policyClass?: string | null;
  layer: string;
  saidAt: string;
  mentions: number;
};

export type TrumpWatchCounts = {
  ratedBuy: number;
  names: number;
  moved: number;
  staleReads: number;
  untagged: number;
  windowDays: number;
  notableSigma: number;
};

/**
 * The four groups, in reading order.
 *
 * ⚠ **The `note` is the point of each heading, not decoration.** "a statement does not change a
 * verdict" is the sentence that stops a reader treating the middle group as a softer buy, and
 * "the screen looked and declined" is what separates it from the group below.
 */
export const GROUPS: { key: string; label: string; note: string; lead?: boolean }[] = [
  {
    key: 'buy',
    label: 'In the screen and rated BUY',
    note: 'the only rows where the screen already says yes',
    lead: true,
  },
  {
    key: 'other',
    label: 'The screen has a different verdict',
    // ⚠ Not "HOLD or REVIEW", which the mockup said. `SELL` also lands here — it exists only for a
    // held name, and the newest run holds exactly one — and a heading naming two of the three
    // would have silently swallowed it.
    note: 'a statement does not change a verdict',
  },
  {
    key: 'screened',
    label: 'Screened, and not picked',
    note: 'the screen looked at this company and declined it',
  },
  {
    key: 'unscreened',
    label: 'Never screened',
    note: 'no verdict exists, so there is nothing here to act on',
  },
];

/** How the verdict word reads in the badge. ⚠ Spelled out, because the word is the channel. */
export const VERDICTS: Record<string, string> = {
  BUY: 'BUY',
  REVIEW: 'REVIEW',
  SELL: 'SELL',
  HOLD: 'HOLD',
  NONE: 'NOT PICKED',
};

export function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 90) return `${Math.round(secs)}s`;
  if (secs < 5400) return `${Math.round(secs / 60)}m`;
  if (secs < 172_800) return `${Math.round(secs / 3600)}h`;
  return `${Math.round(secs / 86_400)}d`;
}

/** `$1.3bn`, or `—`. Compact, because the magnitude is the point and the column is narrow. */
export function cap(value: number | null | undefined): string {
  if (value == null || value <= 0) return '—';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}tn`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}bn`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}m`;
  return `$${value.toFixed(0)}`;
}

/**
 * Why this company is on the page, in the fewest words that stay true.
 *
 * ⚠ **Three different sentences for three different states, and `null` is not `false`.** `exposed`
 * is `true` when the filings carry current evidence, `false` when they were searched and carried
 * none, and `null` when nothing was measured — which is every directly-named company, because
 * naming `INTC` needs no filing search to justify it. Rendering `null` as "not exposed" would
 * report a measurement that was never taken.
 */
export function why(row: TrumpWatchRow): string {
  if (row.exposed === true && row.exposureHits) {
    return `${row.exposureHits} annual filing${row.exposureHits === 1 ? '' : 's'} mention ` +
      `“${row.phrase ?? row.theme}”`;
  }
  if (row.exposed === false) return `searched its filings for “${row.phrase ?? row.theme}” — none`;
  return 'named directly, so no filing search was needed';
}

/** The evidence column: how much filing support, and how recent. */
export function filings(row: TrumpWatchRow): string {
  if (row.exposureHits == null) return 'named directly';
  const when = row.newestFiling ? ` · ${row.newestFiling.slice(0, 7)}` : '';
  return `${row.exposureHits} filing${row.exposureHits === 1 ? '' : 's'}${when}`;
}

/**
 * Which rows the board shows.
 *
 * ⚠ **"In the screen only" hides `unscreened` and nothing else.** It must not also hide `screened`:
 * a company the screen looked at and declined IS in the screen, and that is the row a reader most
 * needs when they are checking whether a name they just heard about has already been assessed.
 *
 * ⚠ The needle searches the statement too, not only the company. A reader who remembers *"the
 * magnets thing"* has the subject, not the ticker.
 */
export function visible(
  rows: TrumpWatchRow[],
  { query, screenOnly }: { query: string; screenOnly: boolean },
): TrumpWatchRow[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (screenOnly && row.verdictGroup === 'unscreened') return false;
    if (!needle) return true;
    return (
      row.ticker.toLowerCase().includes(needle) ||
      row.company.toLowerCase().includes(needle) ||
      row.theme.toLowerCase().includes(needle) ||
      (row.policyClass ?? '').toLowerCase().includes(needle) ||
      row.statement.toLowerCase().includes(needle)
    );
  });
}

/**
 * Companies he has named recently, grouped by what the value screen says about them.
 *
 * ⚠ **The search is client-side, and that is a different call from the statement feed's class
 * filter deliberately.** There the window is a week against a limit of 100, so filtering locally
 * would show *"the trade statements among the newest 100"* rather than the newest 100 trade
 * statements. Here the whole window arrives in one response — one row per company, 200 max — so a
 * local filter cannot misrepresent what it is filtering.
 */
export function TrumpBoard({
  rows,
  counts,
}: {
  rows: TrumpWatchRow[];
  counts?: TrumpWatchCounts | null;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [screenOnly, setScreenOnly] = useState(false);

  const shown = useMemo(() => visible(rows, { query, screenOnly }), [rows, query, screenOnly]);
  const grouped = useMemo(
    () => GROUPS.map((g) => ({ ...g, rows: shown.filter((r) => r.verdictGroup === g.key) })),
    [shown],
  );

  const days = counts?.windowDays ?? 7;
  return (
    <section className="jmt-stk-tb">
      {/* 🔴 The standing caveat, on the screen rather than in a tooltip. This page pairs a
          politician's words with a price beside a BUY, which is the single most misreadable thing
          the platform shows, so the sentence that says what it is not has to be where the reader
          already is. */}
      <p className="jmt-stk-tb__caveat">
        The verdict on the right comes from the <Link to="/screener">value screen</Link>, never from
        the statement. Exposure is a reason to <em>look;</em> the screen is the reason to buy. No
        rule reads this page.
      </p>

      {counts ? (
        <div className="jmt-stk-tb__tiles">
          {/* The one hero on the screen: the only number that is arguably actionable. */}
          <MetricTile
            variant="hero"
            label="Rated BUY"
            value={counts.ratedBuy}
            context={`of ${counts.names} names he touched in ${days} days`}
            translation={
              'How many companies he mentioned recently that the value screen independently ' +
              'rates BUY. The screen decided that on fundamentals, before and regardless of ' +
              'anything he said.'
            }
          />
          <MetricTile
            label="Names touched"
            value={counts.names}
            context={`${days} days · ${counts.untagged} statements named nobody`}
            translation="How many distinct companies appear on this page at all."
          />
          <MetricTile
            /* ⚠ **Spelled out, because `MetricTile` uppercases its label and
               `text-transform: uppercase` turns σ into Σ.** "MOVED OVER 2Σ" is what
               rendered, and Σ is summation — a different symbol with a different meaning,
               in a label about standard deviations. σ is safe in the row cells, which are
               not uppercased. */
            label={`Moved over ${counts.notableSigma} sigma`}
            value={counts.moved}
            context="since the statement, up or down"
            translation={
              'How many moved more than twice their own normal daily swing after he spoke. A ' +
              'large move is not evidence he caused it.'
            }
          />
          <MetricTile
            label="Prices behind"
            value={counts.staleReads}
            context="read later than the freshest on this page"
            /* ⚠ The tile is deliberately not "stale prices". It counts rows whose read lags the
               freshest read HERE, which is a relative measure: when the market is shut every row
               is equally old and this reads 0, because then no row misleads more than its
               neighbours. */
            translation={
              'How many rows carry a price read noticeably older than the newest one on the page. ' +
              'A percentage is only as current as the price under it.'
            }
          />
        </div>
      ) : null}

      <div className="jmt-stk-tb__controls">
        <label className="jmt-stk-tb__search">
          <span className="jmt-sr-only">Search company, ticker or subject</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="company, ticker, subject"
          />
        </label>
        <button
          type="button"
          aria-pressed={screenOnly}
          className={`jmt-stk-tb__toggle${screenOnly ? ' jmt-stk-tb__toggle--on' : ''}`}
          onClick={() => setScreenOnly((on) => !on)}
          title="Hide companies the value screen has never evaluated."
        >
          In the screen only
        </button>
        {shown.length !== rows.length ? (
          <span className="jmt-stk-tb__filtered">
            {shown.length} of {rows.length} shown
          </span>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="jmt-note">
          <EmptyState>
            No company was named in the last {days} days. He averages about four market-relevant
            announcements a trading day, but most are macro — measured, roughly 30% of statements
            name a listed company at all.
          </EmptyState>
        </div>
      ) : null}

      {grouped.map((group) =>
        /* ⚠ An empty group renders nothing rather than an empty box. The middle group is empty
           most days — the newest run holds two REVIEW and one SELL — and four headings above one
           table would read as three failures to load. */
        group.rows.length === 0 ? null : (
          <div key={group.key} className="jmt-stk-tb__group">
            <div
              className={`jmt-stk-tb__glabel${group.lead ? ' jmt-stk-tb__glabel--lead' : ''}`}
            >
              <span className="jmt-stk-tb__gname">{group.label}</span>
              <span className="jmt-stk-tb__grule" />
              <span className="jmt-stk-tb__gnote">{group.note}</span>
            </div>
            <div className="jmt-stk-tb__table" role="table">
              <div className="jmt-stk-tb__head" role="row">
                <span role="columnheader">Company</span>
                <span role="columnheader">Verdict</span>
                {/* ⛔ "Since" and not "impact". The price moved after he spoke; this column cannot
                    say he moved it. */}
                <span role="columnheader">Since stmt</span>
                <span role="columnheader">Price read</span>
                <span role="columnheader">Evidence</span>
                <span role="columnheader">Why it is here</span>
              </div>
              {group.rows.map((row) => (
                <div className="jmt-stk-tb__row" role="row" key={row.ticker}>
                  <span className="jmt-stk-tb__co" role="cell">
                    {/* ⚠ The `title` is not decoration: the column is capped at 210px and
                        "CoreWeave Infrastructure Holdings" ellipsises, so without it a clipped
                        name is unreadable rather than merely abbreviated. */}
                    <span className="jmt-stk-tb__name" title={row.company}>
                      {row.isin ? (
                        <Link to={`/screener/${row.isin}`}>{row.company}</Link>
                      ) : (
                        row.company
                      )}
                    </span>
                    <span className="jmt-stk-tb__sub">
                      {row.ticker} · {cap(row.marketCap)}
                      {/* ⚠ Flagged, never withheld. A cap built on a share count over 400 days old
                          almost always reads SMALL, and small is what a reader hunting exposure
                          wants to believe — `growth.py` records a company ranking first at "$78M"
                          on a pre-merger SPAC count. */}
                      {row.capStale ? (
                        <span
                          className="jmt-stk-tb__flag"
                          title={
                            `The share count behind this is from ${row.shareCountFiled ?? 'an ' +
                            'older filing'} — over 400 days old, so the figure is probably too ` +
                            'small. Shown rather than hidden, because a missing cap and a stale ' +
                            'one are different facts.'
                          }
                        >
                          stale count
                        </span>
                      ) : null}
                      {row.mentions > 1 ? (
                        <span title={`Named in ${row.mentions} statements in this window`}>
                          · ×{row.mentions}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  <span className="jmt-stk-tb__verdict" role="cell">
                    <span
                      className={`jmt-stk-tb__pill jmt-stk-tb__pill--${row.verdictGroup}`}
                      title={row.verdictReason ?? undefined}
                    >
                      {row.verdict ? (VERDICTS[row.verdict] ?? row.verdict) : 'NOT SCREENED'}
                    </span>
                    {row.screenerOn ? (
                      <span className="jmt-stk-tb__on">{row.screenerOn}</span>
                    ) : null}
                  </span>
                  <span className="jmt-stk-tb__move" role="cell">
                    {/* ⚠ **`Number()`, not the string.** `formatNum` returns a string value
                        verbatim and `Num` gates the sign, the colour AND the arrow on `typeof
                        value === 'number'`, so the API's `"2.75"` renders as a bare grey `2.75`
                        with no percent sign and no direction. Caught by screenshot, not by tsc. */}
                    {row.pctSince == null ? (
                      <span className="jmt-stk-tb__unknown" title="No baseline price captured yet.">
                        —
                      </span>
                    ) : Number(row.pctSince).toFixed(1) === '0.0' ? (
                      /* 🔴 **A move that rounds to zero gets no arrow and no colour.** It rendered
                         as `+0.0%▲` in green — a claimed rise, in the profit colour, on a price
                         that had not moved. The rule is that a figure carrying no magnitude must
                         not carry a direction either, and `0.0%` is still a measurement worth
                         printing: it says the market has seen this and done nothing. */
                      <Num
                        value={Number(row.pctSince)}
                        kind="percent"
                        dp={1}
                        colour={false}
                        step="md"
                        style={{ color: 'var(--text-secondary)' }}
                      />
                    ) : (
                      /* ⚠ **`Number()`, not the string.** `formatNum` returns a string value
                         verbatim and `Num` gates the sign, the colour AND the arrow on `typeof
                         value === 'number'`, so the API's `"2.75"` renders as a bare grey `2.75`.
                         ⚠ `md`, not `display`: the ramp allows one `display` per screen and the
                         statement log below already spends it. */
                      <Num value={Number(row.pctSince)} kind="percent" dp={1} arrow step="md" />
                    )}
                    {/* ⚠ σ is null, not 0, when the volatility is unknown. A move of unknown
                        significance is not a move of no significance. */}
                    <span className="jmt-stk-tb__sigma">
                      {/* ⚠ U+2212, the same minus `Num` prints above it. A hyphen here and a
                          true minus one line up read as two different quantities. */}
                      {row.moveSigma == null
                        ? 'σ unknown'
                        : `${Number(row.moveSigma).toFixed(1).replace('-', '−')}σ`}
                    </span>
                  </span>
                  <span className="jmt-stk-tb__read" role="cell">
                    <AgeChip
                      age={ago(row.lastPriceAt)}
                      state={row.readStale ? 'stale' : 'fresh'}
                    />
                  </span>
                  <span className="jmt-stk-tb__filings" role="cell">
                    {filings(row)}
                  </span>
                  <span className="jmt-stk-tb__evidence" role="cell">
                    <span className="jmt-stk-tb__stmt">{row.statement}</span>
                    <span className="jmt-stk-tb__meta">
                      {(row.policyClass ?? 'other').toUpperCase()} · {ago(row.saidAt)} ago ·{' '}
                      {why(row)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ),
      )}

      {counts && counts.untagged > 0 ? (
        /* ⚠ Reporting this is what stops the table reading as everything he said. Most of what he
           says names no listed company at all. */
        <p className="jmt-stk-tb__rest">
          {counts.untagged} statement{counts.untagged === 1 ? '' : 's'} in the last {days} days
          named no listed company — kept for the record, out of the way, in the statement log below.
        </p>
      ) : null}
    </section>
  );
}
